import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { buildSrcdoc } from '../../../lib/build-srcdoc';
import type { ViewerFeatures, PortalTheme } from '../../../lib/portal-contracts';
import {
  useViewerBridge,
  type SerializedMessage,
} from '../../../lib/viewer-bridge';

export interface ReportEditorFrameHandle {
  /** Ask the iframe to serialize its current (edited) document. */
  requestSerialize: () => void;
  /** Revert every unsaved edit back to the loaded version. */
  revertAll: () => void;
  next: () => void;
  prev: () => void;
}

interface Props {
  html: string;
  mode: 'view' | 'edit';
  features: ViewerFeatures;
  theme: PortalTheme;
  /**
   * The agency's `--r-*` report colour tokens (compiled `css_vars`). Injected
   * into the preview like the portal does; never part of the saved HTML.
   */
  reportVars?: Record<string, string> | null;
  title: string;
  /** Fired as the user types in edit mode (unsaved-edit counter). */
  onEditState?: (dirty: number, editable: number) => void;
  /** Fired with the serialized HTML after `requestSerialize()`. */
  onSerialized?: (html: string) => void;
  /** Fired once with slide count + any compat warnings after boot. */
  onCompat?: (slides: number, warnings: string[]) => void;
}

/**
 * `value`, but with the SAME reference for as long as its JSON content is
 * unchanged. The srcdoc is rebuilt (and the iframe reloaded, dropping any
 * unsaved edit) whenever a memo dependency changes identity, so object props a
 * parent re-creates on every render must not count as a change.
 */
function useStableByValue<T>(value: T): T {
  const ref = useRef<{ key: string; value: T } | null>(null);
  const key = JSON.stringify(value ?? null);
  if (!ref.current || ref.current.key !== key) {
    ref.current = { key, value };
  }
  return ref.current.value;
}

/**
 * The sandboxed report frame for the agency workbench.
 *
 * SECURITY INVARIANT (identical to tara-portal ReportViewer): sandbox is
 * exactly "allow-scripts". NEVER add allow-same-origin (script + same-origin =
 * sandbox escape to the tara-client origin + its Redux/session), nor
 * allow-top-navigation / allow-popups — link opening is postMessage-vetted by
 * the parent (useViewerBridge) instead.
 */
const ReportEditorFrame = forwardRef<ReportEditorFrameHandle, Props>(
  function ReportEditorFrame(
    {
      html,
      mode,
      features,
      theme,
      reportVars,
      title,
      onEditState,
      onSerialized,
      onCompat,
    },
    ref
  ) {
    const { t } = useTranslation();
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const handleSerialized = useMemo(
      () => (msg: SerializedMessage) => onSerialized?.(msg.html),
      [onSerialized]
    );

    const bridge = useViewerBridge(iframeRef, { onSerialized: handleSerialized });

    // Surface edit-state + compat upward without leaking the bridge object.
    // The parent keeps these in its own state, so they are reported from
    // effects, after this render, and never while rendering. The callbacks
    // are read through refs: a parent that passes a new function on every
    // render gets one report per change, not one per render.
    const onEditStateRef = useRef(onEditState);
    onEditStateRef.current = onEditState;
    const dirty = bridge.editState?.dirty;
    const editable = bridge.editState?.editable;
    useEffect(() => {
      if (dirty === undefined || editable === undefined) return;
      onEditStateRef.current?.(dirty, editable);
    }, [dirty, editable]);

    const onCompatRef = useRef(onCompat);
    onCompatRef.current = onCompat;
    const compat = bridge.compat;
    const compatReportedRef = useRef<boolean>(false);
    useEffect(() => {
      if (!compat || !onCompatRef.current || compatReportedRef.current) return;
      compatReportedRef.current = true;
      onCompatRef.current(compat.slides, compat.warnings);
    }, [compat]);

    useImperativeHandle(
      ref,
      () => ({
        requestSerialize: bridge.requestSerialize,
        revertAll: bridge.revertAll,
        next: bridge.next,
        prev: bridge.prev,
      }),
      [bridge]
    );

    // Rebuild the srcdoc whenever the html or mode changes — switching to edit
    // mode reloads the iframe with the editable runtime. Features and vars are
    // compared by value so a parent re-render never reloads the frame.
    const stableFeatures = useStableByValue(features);
    const stableVars = useStableByValue(reportVars ?? null);
    const built = useMemo(
      () =>
        buildSrcdoc(
          html,
          { mode, features: stableFeatures, theme },
          stableVars
        ),
      [html, mode, stableFeatures, theme, stableVars]
    );

    return (
      <div className='report-workbench__frame-wrap'>
        <div
          className='report-workbench__frame-stage'
          data-testid='report-workbench-stage'
        >
          <iframe
            ref={iframeRef}
            className='report-workbench__frame'
            title={title}
            sandbox='allow-scripts'
            srcDoc={built.srcdoc}
          />
        </div>
        {/* Below the stage, never over it: an overlay covered the bottom of
            each slide (the cover's last summary line). */}
        {mode === 'view' && bridge.total > 1 && (
          <div
            className='report-workbench__frame-chrome'
            data-testid='report-workbench-pager'
          >
            <button
              type='button'
              className='report-workbench__chrome-btn'
              onClick={bridge.prev}
              disabled={bridge.index <= 0}
              aria-label={t('ReportWorkbenchPrevSlide', 'Previous slide')}
            >
              ‹
            </button>
            <span>
              {bridge.index + 1} / {bridge.total}
            </span>
            <button
              type='button'
              className='report-workbench__chrome-btn'
              onClick={bridge.next}
              disabled={bridge.index >= bridge.total - 1}
              aria-label={t('ReportWorkbenchNextSlide', 'Next slide')}
            >
              ›
            </button>
          </div>
        )}
      </div>
    );
  }
);

export default ReportEditorFrame;
